import axios from 'axios'
import toast from 'react-hot-toast'

// FIXED: Use the correct API base URL - hardcoded to prevent environment variable issues
const API_BASE_URL = 'https://samvadic-platform.vercel.app'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`, // Fixed URL
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common error cases
    if (error.response) {
      const { status, data } = error.response
      
      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('token')
          delete api.defaults.headers.common['Authorization']
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
            toast.error('Session expired. Please login again.')
          }
          break
          
        case 403:
          toast.error('Access forbidden')
          break
          
        case 404:
          toast.error('Resource not found')
          break
          
        case 422:
          // Validation errors
          if (data.detail) {
            if (Array.isArray(data.detail)) {
              data.detail.forEach(err => {
                toast.error(`${err.loc[1]}: ${err.msg}`)
              })
            } else {
              toast.error(data.detail)
            }
          } else {
            toast.error('Validation error')
          }
          break
          
        case 429:
          toast.error('Too many requests. Please slow down.')
          break
          
        case 500:
          toast.error('Server error. Please try again later.')
          break
          
        default:
          toast.error(data.detail || 'An unexpected error occurred')
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.')
    } else {
      // Something else happened
      toast.error('An unexpected error occurred')
    }
    
    return Promise.reject(error)
  }
)

// API methods
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'), // Fixed: use /auth/me
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
}

export const journeyAPI = {
  start: () => api.post('/journey/start'),
  continue: (message) => api.post('/journey/continue', { message }),
  getStatus: () => api.get('/journey/status'),
  getHistory: () => api.get('/journey/history'),
}

export const anchorsAPI = {
  getAll: () => api.get('/anchors'),
  getById: (id) => api.get(`/anchors/${id}`),
  update: (id, data) => api.put(`/anchors/${id}`, data),
  export: (anchorType) => api.post(`/anchors/export`, { anchor_type: anchorType }),
}

export const consultantAPI = {
  ask: (question) => api.post('/consult', { query: question }),
  getHistory: () => api.get('/consultation/history'),
  getContext: () => api.get('/consult/context'),
}

export const documentsAPI = {
  getAll: () => api.get('/documents'),
  getById: (id) => api.get(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
  upload: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

export const pdfAPI = {
  generateBrandStrategy: () => api.post('/pdf/generate', { 
    document_type: 'brand_strategy_pdf' 
  }),
  generatePitchDeck: () => api.post('/pdf/generate', { 
    document_type: 'pitch_deck_pdf' 
  }),
  getStatus: (generationId) => api.get(`/pdf/status/${generationId}`),
  download: (documentId) => api.get(`/pdf/download/${documentId}`, { 
    responseType: 'blob' 
  }),
  getBrandColors: () => api.get('/pdf/brand-colors'),
  getTemplates: () => api.get('/pdf/templates'),
}

export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getPreferences: () => api.get('/users/preferences'),
  updatePreferences: (data) => api.put('/users/preferences', data),
  getStats: () => api.get('/users/stats'),
  getActivity: () => api.get('/users/activity'),
  upgradePremium: () => api.post('/users/upgrade-premium'),
}

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
  info: () => api.get('/'),
  apiInfo: () => api.get('/api'),
}

export default api